import { Buttons } from '../../../../../../../controls/buttons';
import { Checkbox } from '../../../../../../../controls/checkbox';
import { Layouts } from '../../../../../../../controls/layouts';
import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalDialogComponent } from 'src/app/controls/components/advanced/modal-dialog/modal-dialog.component';
import { Password, PasswordComponentValue } from '../../../../../../../controls/password';
import { RadioButtonGroup } from '../../../../../../../controls/radio-button-group';
import { Textbox } from '../../../../../../../controls/textbox';
import { OnboardingCredentials } from 'src/app/partnerportal/components/onboarding-credentials/models/credentials';
import { CredentialsStatus, Transport, TransportType } from 'src/app/partnerportal/models/partner-onboarding';
import { PartnerOnboardingTransportService } from '../../../../services/transport-service';

@Component({
  selector: 'app-ftp-transport-editor',
  standalone: true,
  imports: [ModalDialogComponent, FormsModule, Textbox, RadioButtonGroup, Checkbox, Password, Buttons, ModalDialogComponent, Layouts],
  templateUrl: './ftp-transport-editor.component.html',
  styleUrl: './ftp-transport-editor.component.scss'
})
export class FtpTransportEditorComponent {
  transportService = inject(PartnerOnboardingTransportService);
  private dialog = inject(NgbActiveModal);
  form = viewChild<NgForm>('form');

  transportOriginal = signal<Transport>(null);
  transportInternal = signal<Transport>(null);
  public dialogResult = signal<FtpTransportEditorDialogResult>(null);

  public set transport(transport: Transport) {
    this.transportOriginal.set(transport);
    this.transportInternal.set(transport);
  }

  result = computed(() => {
    const result: FtpTransportResult = {
      transport: this.transportInternal(),
      dialogResult: this.dialogResult()
    }
    return result;
  });

  credentials = computed(() => {
    const field = this.transportInternal()?.TransportFields?.find(f => f.Name === 'Credentials');
    const credentials = field?.Value ? JSON.parse(field.Value) as OnboardingCredentials : null;
    return credentials;
  });

  credentialsPassword = computed(() => {
    const credentials = this.credentials();
    const pcv: PasswordComponentValue = {
      id: credentials?.Password ?? null,
      value: null
    }
    return pcv;
  });

  hostname = computed(() => {
    const field = this.transportInternal()?.TransportFields?.find(f => f.Name === 'Hostname');
    const hostname = field?.Value;
    return hostname;
  });

  port = computed(() => {
    const field = this.transportInternal()?.TransportFields?.find(f => f.Name === 'Port');
    const port = field?.Value;
    return port;
  });

  inboundFolder = computed(() => {
    const field = this.transportInternal()?.TransportFields?.find(f => f.Name === 'InFolder');
    const inboundFolder = field?.Value;
    return inboundFolder;
  });

  outboundFolder = computed(() => {
    const field = this.transportInternal()?.TransportFields?.find(f => f.Name === 'OutFolder');
    const outboundFolder = field?.Value;
    return outboundFolder;
  });

  provideCredentialsNowChanged(provideNow: boolean): void {
    console.log('Provide credentials now changed: ', provideNow);
  }

  async typeChanged(newType: TransportType): Promise<void> {
    const originalType = this.transportOriginal()?.Type;
    if (originalType && newType && originalType !== newType) {
      const newTransport = await this.transportService.generateTransport(newType);
      this.transportInternal.set(newTransport);
    }
    else {
      this.transportInternal.set(this.transportOriginal());
    }
  }

  getTransportFromForm(): Transport {
    const type = this.form().form.get('type')?.value as string;
    const newTransportType = type === 'IntegratedFTP' ? TransportType.IntegratedFTP : TransportType.FTPCustomerEndpoint;
    const oldTransportType = this.transportOriginal()?.Type;

    if (newTransportType === TransportType.IntegratedFTP) {
      if (oldTransportType === TransportType.IntegratedFTP) {
        // Return unmodified transport for integrated FTP if type hasn't changed, as the form doesn't expose any editable fields for it
        return this.transportOriginal();
      }
      else {
        // For new integrated FTP transport, return the generated transport with pre-filled hostname and folders, as the form doesn't expose any editable fields for it.
        // Note that credentials status is set to NotProvided for integrated FTP, as credentials are not provided upfront and must be requested later by the partner.
        return new Transport({ ...this.transportInternal(), CredentialsStatus: CredentialsStatus.NotProvided });
      }
    }
    else {
      let credentials: OnboardingCredentials = null;
      const provideCredentialsNow = this.form().form.get('provideCredentialsNow')?.value as boolean;
      if (provideCredentialsNow) {
        const username = this.form().form.get('field-Username')?.value as string;
        const passwordComponentValue = this.form().form.get('field-Password')?.value as PasswordComponentValue;
        credentials = {
          Username: username,
          Password: passwordComponentValue?.value ? passwordComponentValue.value : passwordComponentValue?.id
        };
      }

      // For non-integrated FTP, return transport with values from the form, as the form exposes editable fields for hostname, credentials, inbound and outbound folders
      const updatedTransport = new Transport({
        ...this.transportInternal(),
        Type: newTransportType,
        CredentialsStatus: provideCredentialsNow && credentials ? CredentialsStatus.Provided : CredentialsStatus.NotProvided,
        TransportFields: this.transportInternal().TransportFields?.map(field => {
          if (field.Name.toLowerCase() === 'credentials') {
            return {
              ...field,
              Value: credentials ? JSON.stringify(credentials) : null
            };
          }
          const value = this.form().form.get('field-'+field.Name)?.value as string;
          return ({
            ...field,
            Value: value
          });
        }) ?? []
      });

      return updatedTransport;
    }
  }

  accept() {
    const updatedTransport = this.getTransportFromForm();
    this.transportInternal.set(updatedTransport);
    this.dialogResult.set(FtpTransportEditorDialogResult.Accept);
    this.dialog.close(this.result());
  }

  cancel() {
    this.dialogResult.set(FtpTransportEditorDialogResult.Cancel);
    this.dialog.close(this.result());
  }
}

export interface FtpTransportResult {
  transport: Transport;
  dialogResult: FtpTransportEditorDialogResult;
}

export enum FtpTransportEditorDialogResult {
  Accept,
  Cancel
}
